import React, { useEffect, useMemo, useState } from 'react'
import {
  IconAlertTriangle,
  IconLock,
  IconMessageChatbot,
  IconPencil,
  IconRefresh,
  IconTrash,
  IconUsers,
} from '@tabler/icons-react'
import { format, formatDistanceToNow, isAfter, parseISO } from 'date-fns'
import { isEmpty, isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { EChart } from '@/components/charts/EChart'
import { Loader } from '@/components/loaders/Loader'
import { Pill } from '@/components/pills/Pill'
import { UserInitial } from '@/components/UserInitial'
import { buildEChartsOption } from '@/views/Kodi/components/KodiChartMessage'

import { CARD_CLOCK_TICK_MS, REPORT_VISIBILITY, SLOT_STATUSES } from './kodiReports.constants'
import { KodiReportShareMenu } from './KodiReportShareMenu'

const isRenderableChart = (chart) => {
  // buildEChartsOption assumes a query_result chart shape with a series
  // array. Real query-engine responses sometimes omit it (table-only
  // results, summary-only payloads). Guard so we fall back to the
  // summary block instead of throwing.
  if (!chart) {
    return false
  }
  if (chart.chart_type === 'pie') {
    return Array.isArray(chart.series) && !isEmpty(chart.series)
  }
  return Array.isArray(chart.series)
}

// Re-renders on a slow tick so "Updated 5 minutes ago" and the refresh cooldown
// stay current while the page sits open.
const useClock = () => {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CARD_CLOCK_TICK_MS)
    return () => clearInterval(timer)
  }, [])
  return now
}

/**
 * One pinned chart on Kodi Reports.
 *
 * @import { KodiReportCard } from '@/views/KodiReports/KodiReportCard'
 * @param {object} props
 * @param {object} props.card The pinned chart's metadata ({ card_id, title, created_at, pinned_by, visibility, shared_with })
 * @param {object} props.cardData Its latest result and load status
 * @param {Function} props.onAsk Opens Kodi with this chart attached
 * @param {Function} props.onEdit Opens the rename modal
 * @param {Function} props.onRefresh Re-runs the chart's query; after a load error, reloads its saved result
 * @param {Function} props.onDelete Removes the chart from Kodi Reports
 * @param {Function} props.onSaveSharing Saves who can see the chart ({ visibility, viewerIds }); resolves true once saved
 * @param {boolean} [props.readOnly] Hides rename, share and remove (a Public report)
 */
export const KodiReportCard = ({
  card,
  cardData,
  onAsk,
  onEdit,
  onRefresh,
  onDelete,
  onSaveSharing,
  readOnly = false,
}) => {
  const { status, chart, summary, error, refreshedAt, nextRefreshAt, refreshError } = cardData
  const title = card.title || chart?.title || 'Untitled chart'
  const now = useClock()

  const option = useMemo(() => {
    if (!isRenderableChart(chart)) {
      return null
    }
    return buildEChartsOption(chart)
  }, [chart])

  const isBusy = status === SLOT_STATUSES.LOADING || status === SLOT_STATUSES.REFRESHING
  const hasResult = status === SLOT_STATUSES.READY || status === SLOT_STATUSES.REFRESHING
  const updatedLabel = isNil(refreshedAt)
    ? null
    : `Updated ${formatDistanceToNow(parseISO(refreshedAt), { addSuffix: true })}`
  const coolingDown = !isNil(nextRefreshAt) && isAfter(parseISO(nextRefreshAt), now)
  // The backend says what this viewer may do. An older one sends neither, and
  // every chart was the whole org's to rename or remove.
  const canEdit = !readOnly && (card.can_edit ?? true)
  const isPrivate = card.visibility === REPORT_VISIBILITY.PRIVATE
  const { shared_with: sharedWithAll = [] } = card
  const sharedWith = card.visibility === REPORT_VISIBILITY.SHARED ? sharedWithAll : []
  const cooldownTooltip = coolingDown
    ? `${updatedLabel}. You can refresh again in ${formatDistanceToNow(parseISO(nextRefreshAt))}.`
    : undefined

  return (
    <Card
      className="kodi-report-card"
      title={title}
      subtitle={updatedLabel}
      buttons={
        <div className="kodi-report-card__actions">
          <Button
            small
            brand
            icon={<IconMessageChatbot />}
            onClick={onAsk}
            data-testid={`kodi-report-card-ask-${card.card_id}`}
          >
            Ask Kodi
          </Button>
          <Button
            square
            small
            basic
            onClick={onRefresh}
            loading={status === SLOT_STATUSES.REFRESHING}
            disabled={isBusy || coolingDown}
            tooltip="Refresh"
            disabledTooltip={cooldownTooltip}
            aria-label="Refresh chart"
            data-testid={`kodi-report-card-refresh-${card.card_id}`}
            icon={<IconRefresh />}
          />
          {canEdit && (
            <Button
              square
              small
              basic
              onClick={onEdit}
              tooltip="Rename"
              aria-label="Rename chart"
              data-testid={`kodi-report-card-edit-${card.card_id}`}
              icon={<IconPencil />}
            />
          )}
          {/* Only its creator changes who sees a chart. */}
          {!readOnly && card.can_share && (
            <KodiReportShareMenu card={card} onSave={onSaveSharing}>
              <Button
                square
                small
                basic
                tooltip="Who can see this"
                aria-label="Share chart"
                data-testid={`kodi-report-card-share-${card.card_id}`}
                icon={<IconUsers />}
              />
            </KodiReportShareMenu>
          )}
          {/* On a chart shared with the viewer, removing only takes it off their page. */}
          {!readOnly && (
            <Button
              square
              small
              basic
              onClick={onDelete}
              tooltip={canEdit ? 'Remove' : 'Remove from my Kodi Reports'}
              aria-label="Remove chart"
              data-testid={`kodi-report-card-delete-${card.card_id}`}
              icon={<IconTrash />}
            />
          )}
        </div>
      }
      pills={
        (isPrivate || !isEmpty(sharedWith) || refreshError) && (
          <div className="flex-align-center small-gap">
            {isPrivate && (
              <Pill
                small
                icon={<IconLock />}
                tooltip="Only you can see this chart."
                dataTestId={`kodi-report-card-private-${card.card_id}`}
              >
                Private
              </Pill>
            )}
            {!isEmpty(sharedWith) && (
              <Pill
                small
                icon={<IconUsers />}
                tooltip={`Shared with ${sharedWith.map((user) => user.name).join(', ')}`}
                dataTestId={`kodi-report-card-shared-${card.card_id}`}
              >
                Shared with {sharedWith.length}
              </Pill>
            )}
            {refreshError && (
              <Pill
                warning
                small
                icon={<IconAlertTriangle />}
                tooltip="The latest refresh failed, so this is the last chart that loaded."
                dataTestId={`kodi-report-card-stale-${card.card_id}`}
              >
                Couldn&apos;t refresh
              </Pill>
            )}
          </div>
        )
      }
    >
      <div className="kodi-report-card__body">
        {status === SLOT_STATUSES.LOADING && (
          <div className="kodi-report-card__center">
            <Loader centered />
          </div>
        )}
        {status === SLOT_STATUSES.ERROR && (
          <div className="kodi-report-card__center kodi-report-card__error">
            <IconAlertTriangle />
            <span>{error || 'Failed to load chart'}</span>
            <Button small onClick={onRefresh}>
              Try again
            </Button>
          </div>
        )}
        {hasResult && option && <EChart option={option} />}
        {hasResult && !option && summary && (
          <div className="kodi-report-card__summary">{summary}</div>
        )}
      </div>
      {card.pinned_by && (
        <footer
          className="kodi-report-card__footer flex-align-center small-gap"
          data-testid={`kodi-report-card-pinned-by-${card.card_id}`}
        >
          <UserInitial userFullName={card.pinned_by.name} index={card.pinned_by.user_id} small />
          <span>
            Pinned by {card.pinned_by.name}
            {card.created_at && ` · ${format(parseISO(card.created_at), 'MMM d, yyyy')}`}
          </span>
        </footer>
      )}
    </Card>
  )
}
